import React, { useContext, useState, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import PropTypes from 'prop-types';
import { Avatar } from '../common/Avatar';
import ComponentTitle from '../common/ComponentTitle';
import ComponentContainer from '../common/ComponentContainer';
import IconButton from '../common/IconButton';
import { H3, H5 } from '../text';
import LayoutContext from '../../contexts/Layout';
import Utils from '../../common/Utils';

const Item = ({ avatarName, avatar, description, id, index, nodeTypeAlias = 'AvatarPlusText', titleSubText, setItemHeights }) => {
    const { layout } = useContext(LayoutContext);

    if (layout.device.isMobile) {
        return (
            <View
                key={id}
                style={{ marginBottom: layout.styleGuide.spacer2 }}
                dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                onLayout={event => {
                    const { height } = event.nativeEvent.layout;

                    setItemHeights(itemHeights => itemHeights.set(index, height + 50));
                }}
            >
                <Avatar imageUri={avatar.cdnImagePath} size={80} />
                <View style={{ alignItems: 'flex-start', justifyContent: 'center' }}>
                    <H3 text={avatarName} />
                    {!!titleSubText && <H5 style={{ color: '#8897a2' }} text={titleSubText} />}
                </View>

                <View style={{ flex: 1 }}>
                    <H5 numberOfLines={null} style={{ color: '#7f868b' }} text={description} />
                </View>
            </View>
        );
    } else {
        return (
            <View
                key={id}
                style={{ marginBottom: layout.styleGuide.spacer2 }}
                dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                onLayout={event => {
                    const { height } = event.nativeEvent.layout;

                    setItemHeights(itemHeights => itemHeights.set(index, height + 50));
                }}
            >
                <View style={{ flexDirection: 'row', marginVertical: layout.styleGuide.spacer2 }}>
                    <Avatar imageUri={avatar.cdnImagePath} size={120} />
                    <View style={{ alignItems: 'flex-start', justifyContent: 'center', marginLeft: layout.styleGuide.spacer1 }}>
                        <H3 text={avatarName} />
                        {!!titleSubText && <H5 style={{ color: '#8897a2' }} text={titleSubText} />}
                    </View>
                </View>

                <H5 numberOfLines={null} style={{ color: '#7f868b' }} text={description} />
            </View>
        );
    }
};

const AvatarPlusTextWithSlider = ({ contentItems = {}, nodeTypeAlias = AvatarPlusTextWithSlider, title, hardWidth }) => {
    const { layout } = useContext(LayoutContext);
    const carouselRef = useRef();
    const [activeIndex, setActiveIndex] = useState(0);
    const items = Object.values(contentItems);
    const carouselWidth = layout.device.isMobile ? hardWidth : Math.floor(hardWidth * 0.9);

    const [itemHeights, setItemHeights] = useState(new Map());

    if (items?.length === 0 || !carouselWidth) {
        return null;
    }

    const sortedItems = items.sort((a, b) => b.description.length - a.description.length);

    return (
        <View
            style={{ flexDirection: 'row', marginBottom: layout.styleGuide.componentSeparator }}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            {!layout.device.isMobile && (
                <IconButton
                    disabled={activeIndex === 0}
                    icon={<MaterialCommunityIcons color={activeIndex === 0 ? '#dde4e9' : '#7f868b'} name='chevron-left-circle-outline' size={38} />}
                    onPress={() => carouselRef.current?.prev()}
                    style={{ margin: 0, paddingHorizontal: 0, paddingVertical: 0 }}
                />
            )}
            <ComponentContainer style={{ flex: 1 }}>
                {!!title && <ComponentTitle title={title} />}
                <View style={{ height: itemHeights.get(carouselRef.current?.getCurrentIndex()) }}>
                    {layout.device.isMobile && (
                        <Carousel
                            autoPlay={false}
                            data={sortedItems}
                            onProgressChange={(_, index) => setActiveIndex(index)}
                            ref={carouselRef}
                            renderItem={({ item, index }) => <Item {...Utils.splitKeyFromProps(item).rest} index={index} setItemHeights={setItemHeights} />}
                            style={{ height: '100%', width: '100%' }}
                            testID='AvatarPlusTextWithSlider.Carousel.Mobile'
                        />
                    )}
                    {!layout.device.isMobile && (
                        <Carousel
                            autoPlay={false}
                            data={sortedItems}
                            onProgressChange={(_, index) => setActiveIndex(index)}
                            ref={carouselRef}
                            renderItem={({ item, index }) => <Item {...Utils.splitKeyFromProps(item).rest} index={index} setItemHeights={setItemHeights} />}
                            style={{ flex: 1 }}
                            testID='AvatarPlusTextWithSlider.Carousel'
                        />
                    )}

                    {sortedItems.length > 1 && (
                        <View style={StyleSheet.flatten(Styles.paginationContainer, { top: layout.device.isMobile ? -10 : undefined })}>
                            {sortedItems.map((item, index) => (
                                <View
                                    key={item.id}
                                    style={StyleSheet.flatten(Styles.paginationDot, {
                                        backgroundColor: index === activeIndex ? '#fffff' : '#dde4e9',
                                        borderColor: index === activeIndex ? '#153d6e' : null,
                                        borderWidth: index === activeIndex ? 1 : 0
                                    })}
                                />
                            ))}
                        </View>
                    )}
                </View>
            </ComponentContainer>
            {!layout.device.isMobile && (
                <IconButton
                    disabled={activeIndex === sortedItems.length - 1}
                    icon={<MaterialCommunityIcons color={activeIndex === sortedItems.length - 1 ? '#dde4e9' : '#7f868b'} name='chevron-right-circle-outline' size={38} />}
                    onPress={() => carouselRef.current?.next()}
                    style={{ margin: 0, paddingHorizontal: 0, paddingVertical: 0 }}
                />
            )}
        </View>
    );
};

const Styles = StyleSheet.create({
    navigationControls: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        top: '40%'
    },
    paginationContainer: {
        alignItems: 'center',
        backgroundColor: 'white',
        flexDirection: 'row',
        justifyContent: 'center'
    },
    paginationDot: {
        borderRadius: 25,
        height: 10,
        marginHorizontal: 2,
        width: 10
    }
});

AvatarPlusTextWithSlider.propTypes = {
    title: PropTypes.string,
    contentItems: PropTypes.object
};

export default AvatarPlusTextWithSlider;
